/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { AppAlert, AppLogo, BlockUI } from "@advapacs/common";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { IRootState } from "../../config/reducers";
import { useAppDispatch } from "../../config/store";
import { complete } from "./ducks/operations";
import { AuthenticationLayout } from "@src/components/AuthenticationLayout";

// Module-level (not component-level) so the guard survives RegisterComplete being
// unmounted/remounted while the app's anonymous session bootstrap (sessionHasBeenFetched
// in App.tsx) toggles - a useRef here would reset on remount and let complete() fire twice.
const dispatchedTokens = new Set<string>();

const RegisterComplete: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("login");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const id = searchParams.get("id");
  const { errorMessage, organisationName, loading } = useSelector(
    (state: IRootState) => state.signUpSlice
  );
  const [organId, setOrganId] = useState("");

  useEffect(() => {
    setOrganId(organisationName);
  }, [organisationName]);

  useEffect(() => {
    const key = `${id}:${token}`;
    if (id && token && !dispatchedTokens.has(key)) {
      dispatchedTokens.add(key);
      dispatch(complete(id, token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  const redirectAfterRegister = useCallback(() => {
    navigate("/", {
      state: organId || null,
    });
  }, [navigate, organId]);
  return (
    <>
      <BlockUI isBlocked={loading}>
        <AuthenticationLayout>
          {errorMessage ? <AppAlert.ErrorAlert error={errorMessage} /> : <></>}

          <div className="mb-0">
            <div className="logo flex-center">
              <AppLogo.CPLogoMain noMargin width={220} height={70} />
            </div>
            <div className="mt-3">
              {!errorMessage && (
                <>
                  <div className="form-group text-center">
                    <h5>{t("congratulation")}</h5>
                  </div>
                  <div className="form-group text-center">
                    <div>{t("register_success_content")}</div>
                  </div>
                </>
              )}
              <div className="form-group text-center">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={redirectAfterRegister}
                >
                  {t("login_now")}
                </button>
              </div>
            </div>
          </div>
        </AuthenticationLayout>
      </BlockUI>
    </>
  );
};

export default RegisterComplete;
